import React, { useState } from 'react';
import {
    Box,
    Button, Checkbox,
    Flex,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Text,
    useClipboard,
    useToast
} from '@chakra-ui/react';
import { RiEyeCloseLine } from "react-icons/ri";
import { MdOutlineRemoveRedEye } from "react-icons/md";

interface ResetPasswordModalProps {
    isOpen: boolean,
    onClose: () => void,
    user: any,
    onReset: (e: {
        isAutoGenerated: boolean,
        password?: string,
        askToChange?: boolean,
    }) => Promise<void>
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose, user, onReset, }) => {
    const textColorSecondary = 'gray.400';
    const toast = useToast();
    const [password, setPassword] = useState('');
    const [show, setShow] = React.useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [method, setMethod] = useState<'auto' | 'custom'>('auto');
    const { onCopy } = useClipboard(password);
    const [askToChange, setAskToChange] = useState(true);

    const handleClick = () => setShow(!show);

    const handleCopyClick = () => {
        onCopy();
        toast({
            title: 'Password is copied to clipboard.',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    const reset = async () => {
        setIsLoading(true);
        await onReset({
            isAutoGenerated: method === 'auto',
            password,
            askToChange,
        });
        handleClose();
        setIsLoading(false);
    }

    const handleClose = () => {
        onClose();
        setMethod('auto');
        setPassword('');
        setShow(false);
        setAskToChange(true);
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Reset password for {user?.firstName} {user?.lastName}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <RadioGroup value={method} onChange={(e: 'auto' | 'custom') => {
                        setMethod(e);
                    }}>
                        <Flex flexDirection='column' gap={2}>
                            <Box>
                                <Radio colorScheme="brandScheme" value='auto'>Automatically generate a password</Radio>
                                <Text ps='24px' color='gray.500' fontSize='sm'>
                                    We will send the auto-generated password
                                    to <b>{user?.firstName} {user?.lastName}</b> via email.
                                </Text>
                            </Box>
                            <Box>
                                <Radio colorScheme="brandScheme" value='custom'>Create password</Radio>
                                <Box ps='24px' hidden={method === 'auto'}>
                                    <HStack>
                                        <InputGroup size="md" me='12px'>
                                            <Input
                                                isRequired={true}
                                                fontSize="sm"
                                                placeholder="Min. 8 characters"
                                                type={show ? 'text' : 'password'}
                                                variant="auth"
                                                onChange={(event) => {
                                                    setPassword(event.target.value);
                                                }}
                                            />
                                            <InputRightElement display="flex" alignItems="center">
                                                <Icon
                                                    color={textColorSecondary}
                                                    _hover={{ cursor: 'pointer' }}
                                                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                                    onClick={handleClick}
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                        <Button
                                            colorScheme="brandScheme"
                                            variant="link"
                                            m={1}
                                            onClick={() => {
                                                handleCopyClick();
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </HStack>
                                    <Checkbox defaultChecked={askToChange} checked={askToChange}
                                              onChange={() => setAskToChange(prev => !prev)} me='16px'
                                              colorScheme='brandScheme'>
                                        <Text color='gray.500' fontSize='sm' mt='4px'>
                                            Ask user to change the password when first sign in
                                        </Text>
                                    </Checkbox>
                                    <Text fontSize='sm' mt='4px' color='gray.600'>
                                        * Password must contain a lowercase, an upper case, a special character or space and at least 8 characters
                                    </Text>
                                </Box>
                            </Box>
                        </Flex>
                    </RadioGroup>
                </ModalBody>
                <ModalFooter>
                    <Button isDisabled={isLoading} isLoading={isLoading} colorScheme="brandScheme" mr={3}
                            onClick={() => reset()}>Reset</Button>
                    <Button colorScheme="brandScheme" variant="ghost" onClick={handleClose}>Cancel</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ResetPasswordModal;
